<template>
  <div class="fixed z-10 inset-0 overflow-y-auto">
    <div
      class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
    >
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span
      >&#8203;

      <div
        class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle max-w-2xl sm:w-full sm:p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-headline"
      >
        <div class="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
          <button
            type="button"
            v-on:click="$emit('toggle-form')"
            class="text-gray-400 hover:text-gray-500 focus:outline-none focus:text-gray-500 transition ease-in-out duration-150"
            aria-label="Close"
          >
            <!-- Heroicon name: x -->
            <svg
              class="h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div class="sm:items-start">
          <div
            class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10"
          >
            <!-- Heroicon name: exclamation -->
            <svg
              class="h-6 w-6 text-yellow-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
            <h3
              class="text-lg leading-6 font-medium text-gray-900"
              id="modal-headline"
            >
              {{ fields.title }}
            </h3>
            <p class="font-light text-gray-700 text-sm">
              {{ fields.subtitle }}
            </p>
            <div class="mt-4">
              <form action="#" method="POST">
                <div class="py-5 bg-white">
                  <div class="grid grid-cols-6 gap-6">
                    <template v-for="field in fields.fields">
                      <template v-if="!field.hidden">
                        <legend-field
                          v-if="field.type === 'legend'"
                          v-bind:key="field.key"
                          :field="field"
                          :formdata="formdata"
                          :formtype="formtype"
                          :params="params"
                        />
                        <text-field
                          v-else-if="field.type === 'text'"
                          v-bind:key="field.key"
                          :field="field"
                          :formdata="formdata"
                          :validation="validation[field.key]"
                        />
                        <number-field
                          v-else-if="field.type === 'number'"
                          v-bind:key="field.key"
                          :field="field"
                          :formdata="formdata"
                          :validation="validation[field.key]"
                        />
                        <select-field
                          v-else-if="field.type === 'select'"
                          v-bind:key="field.key"
                          :field="field"
                          :formdata="formdata"
                          :params="params"
                        />
                        <date-picker-field
                          v-else-if="field.type === 'date'"
                          v-bind:key="field.key"
                          :field="field"
                          :formdata="formdata"
                        />
                        <date-time-picker-field
                          v-else-if="field.type === 'datetime'"
                          v-bind:key="field.key"
                          :field="field"
                          :formdata="formdata"
                          :validation="validation[field.key]"
                        />
                        <radio-field
                          v-if="field.type === 'radio'"
                          v-bind:key="field.key"
                          :field="field"
                          :formdata="formdata"
                        />
                        <search-option-field
                          class="pb-4"
                          v-else-if="field.type === 'searchoption'"
                          v-bind:key="field.key"
                          :field="field"
                          :formdata="formdata"
                          :params="params"
                        />
                      </template>
                    </template>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div class="mt-4 sm:flex sm:flex-row-reverse">
          <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
            <button
              type="button"
              v-on:click="submitForm"
              class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-gray-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-gray-500 focus:outline-none focus:border-gray-700 focus:ring-gray transition ease-in-out duration-150 sm:text-sm disabled:opacity-50"
              :disabled="disabled === true"
            >
              <svg
                v-if="isSubmiting"
                class="animate-spin -ml-1 mr-3 h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>

              {{ $t('message.dinamicform.dinamicform.enviar') }}
            </button>
          </span>
          <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
            <button
              type="button"
              v-on:click="$emit('toggle-form')"
              class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-gray-300 focus:ring-gray transition ease-in-out duration-150 sm:text-sm sm:leading-5"
            >
              {{ $t('message.dinamicform.dinamicform.cancelar') }}
            </button>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import LegendField from '@/components/DynamicForm/LegendField';
import TextField from '@/components/DynamicForm/TextField';
import NumberField from '@/components/DynamicForm/NumberField';
import SelectField from '@/components/DynamicForm/SelectField';
import RadioField from '@/components/DynamicForm/RadioField';
import DatePickerField from '@/components/DynamicForm/DatePickerField';
import DateTimePickerField from '@/components/DynamicForm/DateTimePickerField';
import SearchOptionField from '@/components/DynamicForm/SearchOptionField'
import Axios from 'axios';

export default {
  name: 'dynamic-form',
  components: {
    LegendField,
    TextField,
    NumberField,
    SearchOptionField,
    SelectField,
    DatePickerField,
    DateTimePickerField,
    RadioField
  },
  data() {
    return {
      formdata: {},
      date: new Date(),
      validation: {},
      fields: {},
      isSubmiting: false,
      disabled: true
    };
  },
  props: {
    formtype: {
      type: String,
      required: true
    },
    params: {
      type: Object,
      required: true
    }
  },
  async created() {
    this.getFormFields();
  },
  methods: {
    async getFormFields() {
      // XXX: Aquí no debería ir blueprintid
      const params = {
        params: {
          ...this.params,
          form: this.formtype
        }
      };
      const response = await this.$http.get(
        'forms', params
      );
      this.fields = response.data;
      this.checkRequired()
    },

    updateValue(key, value) {
      this.formdata[key] = JSON.parse(JSON.stringify(value));
      this.formdata = JSON.parse(JSON.stringify(this.formdata));
      this.checkRequired()
    },
    checkRequired() {
      // Check for required
      const requiredlist = this.fields.fields.filter(function (el) {
        return el.required === true
      })

      this.disabled = false
      if (requiredlist.length > 0) {
        requiredlist.forEach(element => {
          if (this.formdata[element.key] === undefined || this.formdata[element.key] === 'Seleccione opción') {
            this.disabled = true
          }
        })
      }

    },
    async submitForm() {
      this.isSubmiting = true;
      const data = {
        formdata: this.formdata,
        params: this.params,
        fields: this.fields.fields
      };

      let payload = {
        form: this.formtype,
        data: data
      };

      const response = await this.$http.post(`forms`, payload);

      this.isSubmiting = false;
      if (this.formtype === 'flowform') {
        this.$router.push('/mtflow/backlog');
      }

      if (response.status === 200) {
        if (response.data.result === 'success') {
          this.$emit('toggle-form');
          if (response.data.filename !== undefined) {
            const filename = response.data.filename;
            this.downloadItem(filename);
          }
        } else {
          // Render validation msgs
          this.validation = response.data;

          // Show hidden fields
          var show = [];
          for (var key in this.validation) {
            show = show.concat(this.validation[key].show);
          }
          const shows = Array.from(new Set(show));

          this.fields.fields.forEach(function(field) {
            if (shows.includes(field.key)) {
              field.hidden = false;
            }
          });
        }
      }
      this.$emit('submit-form');
    },

    // XXX: This should be without a fixed url
    async downloadItem(filename) {
      const url =
        'http://lipigas.mtsolutions.io:8080/xlsxreports/' + filename;
      Axios.get(url, { responseType: 'blob' })
        .then(response => {
          const blob = new Blob([response.data], {
            type: 'application/vnd.ms-excel'
          });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = filename;
          link.click();
          URL.revokeObjectURL(link.href);
        })
        .catch(console.error);
    }
  }
};
</script>
